import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Award, Building, CheckCircle, DollarSign, Mic } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

export default function EarningsSection() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);

  useEffect(() => {
    fetchEarnings();
    checkStripeReturn();
  }, []);

  const fetchEarnings = async () => {
    try {
      const [earningsRes, configRes] = await Promise.all([
        axios.get(`${API}/earnings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/connect/config`).catch(() => ({ data: { configured: false } }))
      ]);
      setEarnings(earningsRes.data);
      setConnectStatus({
        connected: earningsRes.data.stripe_connected,
        payouts_enabled: earningsRes.data.stripe_payouts_enabled,
        account_id: earningsRes.data.stripe_account_id,
        configured: configRes.data.configured,
        config_message: configRes.data.message,
      });
    } catch {} finally { setLoading(false); }
  };

  const checkStripeReturn = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'complete') {
      // Returned from Stripe onboarding - refresh status
      fetchConnectStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('stripe') === 'refresh') {
      // Need to re-do onboarding
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const fetchConnectStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/connect/account-status`, { headers: getAuthHeaders() });
      setConnectStatus(data);
    } catch {}
  };

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      // Step 1: Create account if needed
      let accountRes = await axios.post(`${API}/connect/create-account`, {}, { headers: getAuthHeaders() });
      
      // Step 2: Generate onboarding link
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API}/connect/account-link`, {
        origin_url: originUrl
      }, { headers: getAuthHeaders() });
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to start Stripe onboarding';
      alert(detail);
    } finally {
      setConnectLoading(false);
    }
  };

  const totalEarned = earnings?.total_earned || 0;
  const pendingPayout = earnings?.pending_payout || 0;
  const isConnected = connectStatus?.connected || connectStatus?.payouts_enabled;
  const isPayoutsEnabled = connectStatus?.payouts_enabled;
  const isConfigured = connectStatus?.configured !== false;

  return (
    <div className="space-y-6" data-testid="earnings-section">
      <h2 className="font-display text-2xl font-bold">
        <span className="text-[#FF4343]">Earnings</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#111827] border border-white/10 p-6">
          <span className="font-mono text-3xl font-bold text-[#FF4343]">${totalEarned.toLocaleString()}</span>
          <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-1">Total Earned</p>
        </div>
        <div className="bg-[#111827] border border-white/10 p-6">
          <span className="font-mono text-3xl font-bold text-white">${pendingPayout.toLocaleString()}</span>
          <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-1">Pending Payout</p>
        </div>
        <div className="bg-[#111827] border border-white/10 p-6">
          <span className="font-mono text-3xl font-bold text-[#10B981]">{earnings?.completed_campaigns || 0}</span>
          <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-1">Campaigns Completed</p>
        </div>
      </div>

      {/* Stripe Connect Card */}
      <div className="bg-[#111827] border border-white/10 p-6 border-l-[3px] border-l-[#FF4343]" data-testid="stripe-connect-card">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#FF4343]" />
              Stripe Connect
              {isPayoutsEnabled && (
                <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#10B981]" data-testid="stripe-connected-badge">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                  Connected
                </span>
              )}
              {isConnected && !isPayoutsEnabled && (
                <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#FF4343]" data-testid="stripe-pending-badge">
                  <span className="w-2 h-2 rounded-full bg-[#FF4343] animate-pulse" />
                  Onboarding Incomplete
                </span>
              )}
            </h3>
            {!isConnected ? (
              <p className="text-xs text-[#6B7280] max-w-md mb-4">
                Link your bank account through Stripe to receive direct payouts for completed campaigns. Stripe handles all payment processing securely.
              </p>
            ) : isPayoutsEnabled ? (
              <p className="text-xs text-[#6B7280] max-w-md mb-4">
                Your bank account is connected and verified. Payouts will be deposited directly when campaigns are completed and approved.
              </p>
            ) : (
              <p className="text-xs text-[#6B7280] max-w-md mb-4">
                Your Stripe account was created but onboarding isn't complete. Please finish setup to receive payouts.
              </p>
            )}
          </div>
          {connectStatus?.account_id && (
            <span className="text-xs font-mono text-[#6B7280] bg-white/5 px-2 py-1">{connectStatus.account_id}</span>
          )}
        </div>

        {!isPayoutsEnabled && isConfigured && (
          <button
            onClick={handleConnectStripe}
            disabled={connectLoading}
            data-testid="connect-stripe-btn"
            className="border-2 border-[#FF4343] bg-transparent text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] px-6 py-2.5 text-sm font-medium transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
          >
            {connectLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
                Setting up...
              </>
            ) : isConnected ? (
              'Complete Onboarding'
            ) : (
              'Connect Bank Account'
            )}
          </button>
        )}

        {!isPayoutsEnabled && !isConfigured && (
          <div className="bg-[#FF4343]/5 border border-[#FF4343]/20 p-3" data-testid="stripe-config-notice">
            <p className="text-xs text-[#FF4343]">
              Stripe Connect is being configured by the platform. You'll be able to connect your bank account shortly.
            </p>
          </div>
        )}

        {isPayoutsEnabled && (
          <div className="flex gap-4 text-xs text-[#6B7280]">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> Identity Verified
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> Bank Account Linked
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> Payouts Enabled
            </span>
          </div>
        )}
      </div>

      {/* Payout History */}
      {earnings?.payout_history?.length > 0 && (
        <div className="bg-[#111827] border border-white/10 p-6">
          <h3 className="text-sm font-semibold mb-4">Payout History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payout-history-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-[#6B7280] font-mono text-xs uppercase">Campaign</th>
                  <th className="text-left py-2 text-[#6B7280] font-mono text-xs uppercase">Amount</th>
                  <th className="text-left py-2 text-[#6B7280] font-mono text-xs uppercase">Status</th>
                  <th className="text-left py-2 text-[#6B7280] font-mono text-xs uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {earnings.payout_history.map(p => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="py-2 text-white">{p.campaign_title}</td>
                    <td className="py-2 font-mono text-[#FF4343]">${p.amount?.toLocaleString()}</td>
                    <td className="py-2">
                      <span className={`font-mono text-xs uppercase ${
                        p.status === 'paid' ? 'text-[#10B981]' : 
                        p.status === 'transfer_failed' ? 'text-red-400' : 'text-[#FF4343]'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-2 text-[#6B7280] text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}




// ==================== ENGAGEMENTS SECTION ====================
const ENG_TYPE_ICONS = { appearance: Mic, endorsement: Award, activation: Building };
const ENG_TYPE_COLORS = { appearance: 'text-blue-400 bg-blue-400/10', endorsement: 'text-yellow-400 bg-yellow-400/10', activation: 'text-[#10B981] bg-[#10B981]/10' };

